import { api } from './apiClient';

/**
 * First-party telemetry beacons: page views and client error reports.
 * Every call is fire-and-forget - failures are swallowed so telemetry can
 * never break the app, and with no backend reachable (sandbox mode) each
 * call is effectively a no-op.
 */

let lastTrackedPath = null;

export function trackPageView(pathname) {
  if (typeof pathname !== 'string' || pathname === lastTrackedPath) return;
  lastTrackedPath = pathname;
  api.post('/api/monitor/events/pageview', { path: pathname }).catch(() => {});
}

// Session guards: never re-report the same error, never flood the backend
// from a render loop. Server body cap is 10kb, so truncate client-side too.
const reportedErrors = new Set();
const MAX_REPORTS_PER_SESSION = 20;
let reportCount = 0;

export function reportError({ message, stack, url, type }) {
  if (typeof message !== 'string' || !message.trim()) return;
  if (reportCount >= MAX_REPORTS_PER_SESSION) return;

  const safeMessage = message.slice(0, 500);
  const dedupeKey = `${type || ''}:${safeMessage}`;
  if (reportedErrors.has(dedupeKey)) return;
  reportedErrors.add(dedupeKey);
  reportCount += 1;

  api.post('/api/monitor/events/error', {
    message: safeMessage,
    stack: typeof stack === 'string' ? stack.slice(0, 3000) : undefined,
    url: typeof url === 'string' ? url.slice(0, 200) : undefined,
    type,
  }).catch(() => {});
}

let handlersInstalled = false;

export function installGlobalErrorHandlers() {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (event) => {
    reportError({
      message: event.message || String(event.error || 'Unknown error'),
      stack: event.error?.stack,
      url: window.location.pathname,
      type: 'window-error',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError({
      message: reason?.message || String(reason || 'Unhandled rejection'),
      stack: reason?.stack,
      url: window.location.pathname,
      type: 'unhandledrejection',
    });
  });
}
